import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import type { CargoProblemDetail, CargoProblemMaterial } from '@magimp/shared';
import {
  Banner,
  Button,
  ConfirmModal,
  ErrorState,
  LoadingState,
  MetaStrip,
  PageHeader,
  SectionCard,
  Tabs,
  useNarrow,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { formatDateTime, type AppLocale } from '../../../i18n/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { useCargoProblemLabels } from '../labels.js';
import {
  useCargoProblemAttention,
  useCargoProblemDetail,
  useCargoProblemMaterials,
  useCargoProblemWorkspaceCapabilities,
  useDeleteCargoProblem,
} from '../queries.js';
import { useCargoProblemViewed } from '../useCargoProblemViewed.js';
import { CargoProblemActions } from './CargoProblemActions.js';
import { CargoProblemAuditSection } from './CargoProblemAuditSection.js';
import { CargoProblemCargoCard } from './CargoProblemCargoCard.js';
import { CargoProblemConversation } from './CargoProblemConversation.js';
import { CargoProblemEditor } from './CargoProblemEditor.js';
import { CargoProblemMaterialLibrary } from './CargoProblemMaterialLibrary.js';
import { CargoProblemStatusBadge } from './CargoProblemStatusBadge.js';
import { CargoProblemWorkCard } from './CargoProblemWorkCard.js';
import '../cargo-problems.css';
import '../cargo-problem-workspace.css';

type WorkspacePane = 'conversation' | 'materials';

// WHY: the same query the library opens with, so reading the brief files off it
// costs no second request — only the client-side split by source.
const ALL_MATERIALS = { filter: 'ALL' } as const;
const EMPTY_MATERIALS: readonly CargoProblemMaterial[] = [];

export function CargoProblemDetailPage() {
  const id = useParams().id ?? '';
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  const narrow = useNarrow();
  const showToast = useToastStore((state) => state.show);
  const meId = useAuthStore((state) => state.user?.id ?? null);
  const detail = useCargoProblemDetail(id);
  const workspace = useCargoProblemWorkspaceCapabilities(id);
  const attention = useCargoProblemAttention();
  const materials = useCargoProblemMaterials(id, ALL_MATERIALS);
  const remove = useDeleteCargoProblem(id);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pane, setPane] = useState<WorkspacePane>('conversation');
  const [jumpToFileId, setJumpToFileId] = useState<string | null>(null);
  const workCardRef = useRef<HTMLDivElement>(null);
  useCargoProblemViewed(id);

  if (detail.isPending || workspace.isPending)
    return (
      <div className="cargo-problem-detail" data-testid="cargo-problem-detail-page">
        <PageHeader title={t('title')} narrow={narrow} />
        <LoadingState />
      </div>
    );
  if (detail.isError || workspace.isError || !detail.data || !workspace.data)
    return (
      <div className="cargo-problem-detail" data-testid="cargo-problem-detail-page">
        <PageHeader title={t('title')} narrow={narrow} />
        <ErrorState />
      </div>
    );

  const problem = detail.data.problem;
  const capabilities = workspace.data.capabilities;
  const locale = i18n.language as AppLocale;
  const unreadCount = attention.data?.items.find((item) => item.problemId === id)?.unreadCount ?? 0;
  const briefFiles =
    materials.data?.items.filter((file) => file.context === 'BRIEF') ?? EMPTY_MATERIALS;

  async function deleteProblem() {
    try {
      await remove.mutateAsync();
      showToast({ tone: 'success', text: t('toasts.deleted') });
      navigate('/cargo/problems', { replace: true });
    } catch (error) {
      showToast({ tone: 'error', text: errorMessage(error) });
    }
  }

  // WHY: a brief file was filed with the problem itself and never travelled
  // through the conversation, so the stream holds no row that could publish it.
  // Its source is the work card, which carries the brief tiles that open it, and
  // the reader is taken there — moving focus, not only the scroll position, so
  // the answer also reaches the keyboard.
  function jumpTo(material: CargoProblemMaterial) {
    if (material.context === 'BRIEF') {
      workCardRef.current?.scrollIntoView({ block: 'start' });
      workCardRef.current?.focus();
      return;
    }
    setPane('conversation');
    setJumpToFileId(material.id);
  }

  const conversationVisible = !narrow || pane === 'conversation';
  const conversation = (
    <CargoProblemConversation
      problemId={id}
      meId={meId}
      capabilities={capabilities}
      unreadCount={unreadCount}
      visible={conversationVisible}
      jumpToFileId={jumpToFileId}
      onJumpResolved={() => setJumpToFileId(null)}
    />
  );
  const library = (
    <CargoProblemMaterialLibrary
      problemId={id}
      canDelete={capabilities.canDeleteAttachment}
      onJump={jumpTo}
    />
  );

  return (
    <div className="cargo-problem-detail" data-testid="cargo-problem-detail-page">
      <PageHeader
        title={
          <span className="cargo-problem-detail-title">
            <span>{problem.publicCode}</span>
            <CargoProblemStatusBadge status={problem.status} />
          </span>
        }
        subtitle={labels.category(problem.category)}
        meta={<CargoProblemHeaderMeta problem={problem} locale={locale} />}
        narrow={narrow}
        actions={
          <>
            <Button kind="ghost" icon="arrow-left" onClick={() => navigate('/cargo/problems')}>
              {t('actions.back')}
            </Button>
            <CargoProblemActions
              problem={problem}
              capabilities={capabilities}
              onEdit={() => setEditing(true)}
            />
          </>
        }
      />
      <div className="cargo-problem-detail-body">
        <div className="cargo-problem-detail-main">
          {isTerminal(problem.status) ? (
            <Banner tone="info" icon="lock">
              {t('detail.terminalReadOnly')}
            </Banner>
          ) : null}
          <div ref={workCardRef} tabIndex={-1} role="group" aria-label={t('detail.workCard')}>
            <CargoProblemWorkCard
              problem={problem}
              capabilities={capabilities}
              briefFiles={briefFiles}
              deleting={remove.isPending}
              onDelete={() => setConfirmDelete(true)}
            />
          </div>
          {narrow ? (
            <>
              <CargoProblemCargoCard problem={problem} />
              <Tabs
                tabs={[
                  { id: 'conversation', label: t('workspace.conversation') },
                  { id: 'materials', label: t('workspace.materials') },
                ]}
                value={pane}
                onChange={setPane}
                aria-label={t('detail.workCard')}
              />
            </>
          ) : null}
          {/* WHY: the conversation keeps one child slot across both layouts and
              stays mounted under the narrow tabs. Unmounting it would throw away
              the typed message, reply target and already-uploaded objects.
              `hidden` keeps the inactive pane out of the accessibility tree and,
              through `visible`, out of read tracking. */}
          <div className="cargo-problem-detail-pane" hidden={!conversationVisible}>
            {conversation}
          </div>
          {narrow ? (
            <div className="cargo-problem-detail-pane" hidden={conversationVisible}>
              {library}
            </div>
          ) : null}
          <CargoProblemAuditSection problemId={id} />
        </div>
        {narrow ? null : (
          <aside>
            <CargoProblemCargoCard problem={problem} />
            <SectionCard title={t('library.title')}>{library}</SectionCard>
          </aside>
        )}
      </div>
      {editing ? <CargoProblemEditor problem={problem} onClose={() => setEditing(false)} /> : null}
      {confirmDelete ? (
        <ConfirmModal
          title={t('delete.title')}
          text={t('delete.text')}
          confirmLabel={t('actions.delete')}
          danger
          onConfirm={() => void deleteProblem()}
          onClose={() => setConfirmDelete(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * The three facts the workspace exists to answer — who is responsible, how
 * much time is left, which queue owns it — stay on the header line. Author and
 * timestamps are provenance: wanted when a dispute is examined, not while the
 * problem is worked, so they sit behind a disclosure rather than a tooltip,
 * which a phone reader could never open.
 */
function CargoProblemHeaderMeta({
  problem,
  locale,
}: {
  problem: CargoProblemDetail;
  locale: AppLocale;
}) {
  const { t } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  const [showProvenance, setShowProvenance] = useState(false);
  return (
    <div className="cargo-problem-header-meta">
      <MetaStrip
        ariaLabel={t('detail.summary')}
        segments={[
          {
            id: 'assignee',
            label: t('detail.assignee'),
            value: problem.assignee?.name ?? t('unassigned'),
          },
          {
            id: 'reactionDueAt',
            label: t('detail.reactionDueAt'),
            value: (
              <span className={problem.overdue ? 'cargo-problems-overdue' : undefined}>
                {problem.reactionDueAt
                  ? formatDateTime(problem.reactionDueAt, locale)
                  : t('detail.notApplicable')}
              </span>
            ),
          },
          { id: 'queue', label: t('detail.queue'), value: labels.queue(problem.queue) },
        ]}
      />
      <Button
        size="sm"
        kind="ghost"
        trailingIcon="chevron-down"
        aria-expanded={showProvenance}
        onClick={() => setShowProvenance((open) => !open)}
      >
        {t('detail.provenance')}
      </Button>
      {showProvenance ? (
        <MetaStrip
          ariaLabel={t('detail.provenance')}
          segments={[
            { id: 'author', label: t('detail.author'), value: problem.author.name },
            {
              id: 'createdAt',
              label: t('detail.createdAt'),
              value: formatDateTime(problem.createdAt, locale),
            },
            {
              id: 'updatedAt',
              label: t('detail.updatedAt'),
              value: formatDateTime(problem.updatedAt, locale),
            },
          ]}
        />
      ) : null}
    </div>
  );
}

function isTerminal(status: CargoProblemDetail['status']) {
  return status === 'NOT_RESOLVED' || status === 'PARTIALLY_RESOLVED' || status === 'RESOLVED';
}
